import React, { useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';

import CrossIcon from '../../../assets/icons/cross.svg';
import LinearProgress from '../LinearProgress';
import Modal from '../Modal';
import PhotoUpload from '../PhotoUpload';
import { OutlinedButton } from '../Buttons';
import styles from './ReplaceThumbnailModal.module.scss';

type ReplaceThumbnailModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onUpload: (data: FormData) => Promise<any>;
  onUploaded: (data: any) => void;
  title?: string;
  cropTitle?: string;
  cropMode?: 'square' | 'landscape';
  onRestore?: () => Promise<any>;
  restoreLabel?: string;
  preview?: {
    type: 'folder' | 'file';
    title: string;
    imageUrl?: string;
  };
};

const ReplaceThumbnailModal: React.FC<ReplaceThumbnailModalProps> = ({
  open,
  setOpen,
  onUpload,
  onUploaded,
  title = 'Replace Thumbnail Image',
  cropTitle = 'Crop thumbnail',
  cropMode = 'landscape',
  onRestore,
  restoreLabel = 'Restore Original Thumbnail',
  preview,
}) => {
  const notification = useSnackbar();
  const [cropping, setCropping] = useState(false);
  const [uploading, setUploading] = useState(false);
  const close = () => {
    if (uploading) return;
    setCropping(false);
    setOpen(false);
  };

  const restore = async () => {
    if (!onRestore) return;
    try {
      setUploading(true);
      const res = await onRestore();
      onUploaded(res.data);
      notification.showMessage('Original image restored');
      setCropping(false);
      setOpen(false);
    } catch {
      notification.showMessage('Original image could not be restored');
    } finally {
      setUploading(false);
    }
  };

  if (!open) return null;

  return (
    <Modal open onClose={close}>
      {uploading && <LinearProgress />}
      <div className={styles.container} onClick={(event) => event.stopPropagation()}>
        <div className={styles.title}>
          {cropping ? cropTitle : title}
          <img src={CrossIcon} alt="close" onClick={close} />
        </div>
        <PhotoUpload
          setOpen={(nextOpen) => {
            if (!nextOpen) setCropping(false);
            setOpen(nextOpen);
          }}
          setCropAvatar={setCropping}
          setIsFileUploading={setUploading}
          onUpload={onUpload}
          onUploaded={onUploaded}
          saveLabel="Save image"
          cropMode={cropMode}
          preview={preview}
        />
        {!cropping && onRestore && (
          <OutlinedButton kind="default" onClick={restore} className={styles.restoreButton}>
            {restoreLabel}
          </OutlinedButton>
        )}
      </div>
    </Modal>
  );
};

export default ReplaceThumbnailModal;
